// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DEFAULT_UNIT_OF_MEASURE } from '@/constants/unit-of-measure';
import { getItems } from '@/items/api/get-items';
import {
  BulkItemAutocomplete,
  CREATE_ITEM_OPTION_ID,
} from '@/items/components/bulk-item-autocomplete';
import { SparseItem } from '@/sparse/api/types';
import { FilterOptionsState, createFilterOptions } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ForwardedRef, forwardRef } from 'react';

export type BulkItemFilterCallback = (options: SparseItem[]) => SparseItem[];

const filter = createFilterOptions<SparseItem>();

function compareItems(item: SparseItem, other: SparseItem) {
  return item.name.localeCompare(other.name);
}

/**
 * Filters the item options based on the provided filter callback and hidden items.
 *
 * @param items - The full list of items to filter
 * @param itemFilter - Optional callback to apply custom filtering logic
 * @param hiddenItems - Items to exclude from the dropdown (e.g., items already
 *   selected in a BOM to prevent duplicates)
 */
function getOptions(
  items: SparseItem[] = [],
  itemFilter: BulkItemFilterCallback | undefined,
  hiddenItems: SparseItem[] | undefined
) {
  let filteredItems = items;

  if (hiddenItems && hiddenItems.length > 0) {
    const hiddenItemIds = new Set(hiddenItems.map((item) => item.id));
    filteredItems = filteredItems.filter((item) => !hiddenItemIds.has(item.id));
  }

  if (itemFilter === undefined) {
    return filteredItems;
  }

  return itemFilter(filteredItems);
}

interface Props {
  name?: string;
  label?: string;
  error?: boolean;
  disabled?: boolean;
  helperText?: string;
  /** Optional callback to apply custom filtering logic to the item options. */
  filter?: BulkItemFilterCallback;
  /**
   * Items to exclude from the dropdown options.
   * Used in BOM editing to hide items that are already selected in other lines,
   * preventing duplicate items in the same bill of materials.
   */
  hiddenItems?: SparseItem[];
  value: SparseItem | null;
  onChange: (value: SparseItem | null) => void;
  fullWidth?: boolean;
  placeholderText?: string;
  showSearchAdornment?: boolean;
}

/**
 * Opens the items page in a new tab with a "new" query parameter.
 */
function openAddItemPage(name: string) {
  const url = new URL('/catalog/items', document.location.origin);
  url.searchParams.append('new', encodeURIComponent(name));
  window.open(url, '_blank');
}

/**
 * Converts an Item from the API to SparseItem format.
 * Bulk items are always physical, without serialized assets.
 */
function itemToSparseItem(item: {
  id: string;
  key: string;
  name: string;
  sku: string;
  isArchived: boolean;
  shouldRenderSerialFields: boolean;
}): SparseItem {
  return {
    id: item.id,
    key: item.key,
    name: item.name,
    type: 'BULK',
    sku: item.sku,
    isArchived: item.isArchived,
    unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
    hasAssets: false,
    isPhysical: true,
    shouldRenderSerialFields: item.shouldRenderSerialFields,
  };
}

function BulkItemSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { data, isLoading } = useQuery({
    queryKey: ['item', 'BULK'],
    queryFn: () => getItems({ type: 'BULK' }),
  });

  // Convert items to SparseItem format
  const items = getOptions(
    data?.results.map(itemToSparseItem),
    props.filter,
    props.hiddenItems
  )
    .slice()
    .sort(compareItems);

  const filterOptions = (
    options: SparseItem[],
    state: FilterOptionsState<SparseItem>
  ) => {
    const filtered = filter(options, state);
    const inputValue = state.inputValue.trim();

    // Add create new option if there's input and no exact match
    if (
      inputValue &&
      !filtered.some(
        (item) => item.name.toLowerCase() === inputValue.toLowerCase()
      )
    ) {
      filtered.push({
        id: CREATE_ITEM_OPTION_ID,
        key: CREATE_ITEM_OPTION_ID,
        name: inputValue,
        type: 'BULK',
        sku: '',
        isArchived: false,
        unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
        hasAssets: false,
        isPhysical: true,
        shouldRenderSerialFields: false,
      });
    }

    return filtered;
  };

  const handleChange = (_: unknown, value: SparseItem | string | null) => {
    if (typeof value === 'string') {
      return openAddItemPage(value);
    }

    if (value === null) {
      return props.onChange(null);
    }

    if (value.id === CREATE_ITEM_OPTION_ID) {
      openAddItemPage(value.name);
      return;
    }

    props.onChange(value);
  };

  return (
    <BulkItemAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={items}
      loading={isLoading}
      onChange={handleChange}
      disabled={props.disabled}
      helperText={props.helperText}
      filterOptions={filterOptions}
      loadingText="Loading..."
      fullWidth={props.fullWidth}
      placeholder={props.placeholderText}
      showSearchAdornment={props.showSearchAdornment}
    />
  );
}

/**
 * A controlled component for selecting a bulk item from the Item Catalog.
 * Returns SparseItem format for compatibility with inventory dialogs.
 */
export const BulkItemSearch = forwardRef(BulkItemSearchRenderer);
